<template>
  <div class="ppa" :class="`ppa--${layout}`" data-test="pp-actions">
    <!-- Tablet and phone — the design's big tiles: Follow-up, then EVERY other action (no fold menu). -->
    <div v-if="isTouch" class="ppa__tiles" data-test="pp-actions-tiles">
      <button
        type="button"
        class="ppa__btn ppa__btn--primary ppa__start"
        :disabled="!byKey.followup.enabled"
        :title="byKey.followup.enabled ? null : 'Coming soon'"
        data-test="pp-act-followup"
        @click="emit(byKey.followup)"
      ><i :class="`fa ${byKey.followup.icon}`" aria-hidden="true"></i>{{ byKey.followup.short }}</button>

      <div class="ppa__grid">
        <button
          v-for="a in tileActions"
          :key="a.key"
          type="button"
          class="ppa__tile"
          :class="{ 'is-off': !a.enabled, 'is-open': whyKey === a.key }"
          :aria-disabled="a.enabled ? null : 'true'"
          :aria-expanded="a.enabled ? null : (whyKey === a.key ? 'true' : 'false')"
          :data-test="`pp-act-${a.key}`"
          @click="tap(a)"
        >
          <i :class="`fa fa-lg ${a.busy ? 'fa-spinner fa-spin' : a.icon}`" aria-hidden="true"></i>
          <span class="ppa__tile-label">{{ a.short }}</span>
          <i v-if="!a.enabled && a.why" class="fa fa-question-circle ppa__q" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Touch has no hover: tapping an unavailable tile shows Patient Profile's reason card, in place. -->
      <div v-if="whyAction && whyAction.why" class="ppa__why" :data-test="`pp-why-${whyAction.key}`">
        <ActionWhy :why="whyAction.why" :icon="whyAction.icon" :surface="surface" />
      </div>
    </div>

    <!-- Desktop — the design's bar. It WRAPS onto a second row when it does not fit. -->
    <div v-else class="ppa__bar" data-test="pp-actions-bar">
      <span class="ppa__wrap">
        <button
          type="button"
          class="ppa__btn ppa__btn--primary"
          :disabled="!byKey.followup.enabled"
          :title="byKey.followup.enabled ? null : 'Coming soon'"
          data-test="pp-act-followup"
          @click="emit(byKey.followup)"
        ><i :class="`fa ${byKey.followup.icon}`" aria-hidden="true"></i>{{ byKey.followup.short }}<i class="fa fa-angle-down" aria-hidden="true"></i></button>
      </span>

      <div class="ppa__group">
        <span class="ppa__group-label">Send</span>
        <span
          v-for="a in sendActions"
          :id="a.enabled ? null : `pp-act-wrap-${a.key}`"
          :key="a.key"
          class="ppa__wrap"
        >
          <button
            type="button"
            class="ppa__btn ppa__btn--in-group"
            :disabled="!a.enabled"
            :data-test="`pp-act-${a.key}`"
            @click="emit(a)"
          ><i :class="`fa ${a.icon}`" aria-hidden="true"></i>{{ a.short }}<i v-if="a.why" class="fa fa-question-circle ppa__q" aria-hidden="true"></i></button>
        </span>
      </div>

      <span class="ppa__div" aria-hidden="true"></span>

      <span
        v-for="a in barActions"
        :id="a.enabled ? null : `pp-act-wrap-${a.key}`"
        :key="a.key"
        class="ppa__wrap"
      >
        <button
          type="button"
          class="ppa__btn"
          :disabled="!a.enabled"
          :data-test="`pp-act-${a.key}`"
          @click="emit(a)"
        >
          <i v-if="a.busy" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          <i v-else :class="`fa ${a.icon}`" aria-hidden="true"></i>{{ a.short }}<i v-if="a.why" class="fa fa-question-circle ppa__q" aria-hidden="true"></i>
        </button>
      </span>

      <!-- Patient Profile's disabled-button popovers, same content. Hover / focus, as there. -->
      <b-popover
        v-for="a in popoverActions"
        :key="`pop-${a.key}`"
        :target="`pp-act-wrap-${a.key}`"
        triggers="hover focus"
        placement="bottom"
        custom-class="pp-why-popover"
      >
        <ActionWhy :why="a.why" :icon="a.icon" :surface="surface" />
      </b-popover>
    </div>
  </div>
</template>

<script>
/**
 * The Patient Panel's action bar (Schedule View Phase 8) — the design's `tm` bar, two layouts:
 *   desktop  Follow-up ▾ · SEND [Form Text Email] | Tx Plans · Create Task · Review · Photo to OD · Form QR · Payment
 *            — one row that WRAPS to a second when the panel is too narrow (never scrolls sideways)
 *   tablet / phone  (design "Patient Action Panels" 3a / 3b, 10/9) Follow-up as a big button, then a
 *            grid of big tiles — EVERY other action, no "Actions" fold menu
 *
 * Presentational: it draws `actions` (buildActions() in src/utils/patientPanel/actionGates.js —
 * Patient Profile's own gates, reasons and upsells) and emits `action` with the key of an ENABLED
 * one. PatientPanelActionHost.vue does the work. Follow-up is enabled only when the host can start
 * one (`followupReady` — the Patient Profile page); on Schedule View it stays "coming soon".
 *
 * An unavailable desktop button is disabled with Patient Profile's popover (hover / focus, on a
 * wrapping span, because a disabled button fires no mouse events). A tile can't use hover on a
 * touch screen, so tapping an unavailable one opens the same card in place, under the grid.
 */
import ActionWhy from './ActionWhy.vue';

/** Same breakpoint as the rest of the panel ($pp-phone-max) and Schedule View's phone timeline. */
const PHONE_QUERY = '(max-width: 599.98px)';
/** Below this the bar becomes tiles (tablets and phones). Mirrors $ap-touch-max in actions/_action-panel.scss. */
const COMPACT_QUERY = '(max-width: 1279.98px)';

function matches(query) {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

export default {
  name: 'PatientPanelActions',
  components: { ActionWhy },

  props: {
    /** buildActions(): [{ key, label, short, icon, enabled, why, hidden, busy, group, primary, soon }] */
    actions: { type: Array, required: true },
    /** Which page draws the bar. It names the page in a "request this add-on" email to Teamio. */
    surface: { type: String, default: 'Schedule View' },
  },

  data() {
    return { layout: 'desktop', whyKey: null };
  },

  computed: {
    visible() {
      return this.actions.filter(a => !a.hidden);
    },
    byKey() {
      const out = {};
      this.actions.forEach((a) => { out[a.key] = a; });
      return out;
    },
    /** Tablet and phone draw tiles; desktop draws the bar. */
    isTouch() {
      return this.layout !== 'desktop';
    },
    sendActions() {
      return this.visible.filter(a => a.group === 'send');
    },
    /** Desktop, after the divider: Tx Plans, Create Task, Review, Photo to OD, Form QR, Payment. */
    barActions() {
      return this.visible.filter(a => a.key !== 'followup' && a.group !== 'send');
    },
    /** Tablet / phone: every action but Follow-up (its own big button), in buildActions() order. */
    tileActions() {
      return this.visible.filter(a => a.key !== 'followup');
    },
    /** The tile whose reason card is open (tapped while unavailable). */
    whyAction() {
      return this.whyKey ? this.byKey[this.whyKey] || null : null;
    },
    /** Disabled inline buttons that have something to explain. */
    popoverActions() {
      return this.visible.filter(a => !a.enabled && a.why && a.key !== 'followup');
    },
  },

  mounted() {
    this.readLayout();
    window.addEventListener('resize', this.readLayout);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.readLayout);
  },

  methods: {
    readLayout() {
      const next = matches(PHONE_QUERY) ? 'phone' : (matches(COMPACT_QUERY) ? 'tablet' : 'desktop');
      if (next !== this.layout) {
        this.layout = next;
        this.whyKey = null;
      }
    },

    emit(a) {
      if (!a.enabled) return;
      this.whyKey = null;
      this.$emit('action', a.key);
    },

    /** A tap on a tile: run it, or explain why it can't run. */
    tap(a) {
      if (a.enabled) {
        this.emit(a);
        return;
      }
      this.whyKey = this.whyKey === a.key ? null : a.key;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

// The design's .tm-actionbar / .tm-btn / .tm-group (styles.css), in the panel's tokens; the tiles are
// the Patient Action Panels design's 3a / 3b grid.

.ppa {
  position: relative;
  margin-top: 14px;
}

.ppa__bar {
  display: flex;
  align-items: center;
  // Wraps to a second row when the panel is too narrow — a bar that scrolled sideways hid buttons
  // (and ran past the edge at 1280px beside the app-shell sidebar).
  flex-wrap: wrap;
  gap: 8px 7px;
  padding: 2px 0 2px;
}

.ppa__wrap {
  display: inline-flex;
  flex-shrink: 0;
}

.ppa__btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid $pp-line;
  border-radius: 10px;
  background: $pp-surface;
  color: $pp-ink;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;

  i { color: $pp-brand; }

  &:hover:not(:disabled) {
    background: $pp-bg;
    border-color: $pp-ink-4;
  }

  &:disabled,
  &.is-off {
    cursor: not-allowed;
    color: $pp-ink-4;
    background: $pp-bg-2;

    i { color: $pp-ink-4; }
  }
}

.ppa__btn--primary {
  height: 38px;
  padding: 0 14px;
  gap: 7px;
  border-color: $pp-brand-deep;
  background: $pp-brand-deep;
  color: #fff;
  font-weight: 700;
  // Design 1a: the one filled button carries a soft teal shadow, deeper on hover.
  box-shadow: $pp-shadow-primary;
  transition: background 0.12s ease, box-shadow 0.12s ease;

  i { color: #fff; }

  &:hover:not(:disabled) {
    background: $pp-brand-deeper;
    border-color: $pp-brand-deeper;
    box-shadow: $pp-shadow-primary-hov;
  }

  // Follow-up is "Coming soon" (Phase 9): drawn in the design's place, clearly not live.
  &:disabled {
    opacity: 0.55;
    color: #fff;
    background: $pp-brand-deep;
    border-color: $pp-brand-deep;
    box-shadow: none;

    i { color: #fff; }
  }
}

.ppa__q {
  margin-left: -2px;
  font-size: 12px;
  color: $pp-attn-ink !important;
}

.ppa__group {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 3px;
  border: 1px solid $pp-line;
  border-radius: 11px;
  // Design 1a: the SEND cluster sits in a faint neutral box.
  background: $pp-bg-2;
}

.ppa__group-label {
  padding: 0 6px 0 8px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $pp-ink-4;
}

.ppa__btn--in-group {
  height: 30px;
  padding: 0 9px;
  border: 0;
  border-radius: 8px;
  font-size: 12.5px;
  gap: 6px;
}

.ppa__div {
  width: 1px;
  height: 24px;
  margin: 0 2px;
  flex-shrink: 0;
  background: $pp-line;
}

.ppa__why {
  margin: 8px 0 0;
  border: 1px solid $pp-line;
  border-radius: 12px;
  background: $pp-surface;
  overflow: hidden;
}

// Tablet and phone — Follow-up as a big button over a grid of big tiles.
.ppa__tiles {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ppa__start {
  width: 100%;
  height: 48px;
  justify-content: center;
  gap: 8px;
  border-radius: 12px;
  font-size: 15px;
}

.ppa__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(68px, 1fr));
  gap: 6px;
}

.ppa__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 68px;
  padding: 8px 4px;
  border: 1px solid $pp-line;
  border-radius: 12px;
  background: $pp-surface;
  color: $pp-ink-2;
  text-align: center;
  cursor: pointer;

  &:active { background: $pp-bg; }

  &.is-off {
    color: $pp-ink-4;
    background: $pp-bg-2;
  }

  &.is-open {
    background: $pp-bg-3;
    border-color: $pp-ink-4;
  }

  .ppa__q {
    position: absolute;
    top: 5px;
    right: 6px;
    margin: 0;
    font-size: 11px;
  }
}

.ppa__tile-label {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  overflow-wrap: anywhere;
}
</style>

<style lang="scss">
// The popover itself is rendered by BootstrapVue into <body>, outside this component's scope —
// so its frame is styled globally, by its own class (the card inside is ActionWhy's, scoped).
// Patient Profile's .disabled-feature-popover frame, including its phone rule: centred on the
// screen, because anchored to a small button it ran off the glass (2026-05-04).
.popover.pp-why-popover {
  max-width: 340px;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

  .popover-body { padding: 0; }

  @media (max-width: 767.98px) {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: calc(100vw - 32px);
    max-width: 360px;

    .arrow { display: none; }
  }
}
</style>
